import { ToucanComponents } from '@jointoucan/toucan-design';
import { Alert, AlertTitle } from '@mui/material';

import { Language, SupportedSourceLanguages } from '~/constants/languages';
import { useGetExtensionUserQuery } from '~/extension/schema';
import { trackEvent } from '~/helpers/analytics';
import { useExtension } from '~/hooks/useExtension';
import { useFirefoxExtension } from '~/hooks/useFirefoxExtension';
import { useLanguageInfo } from '~/hooks/useLanguageInfo';
import { useLanguageSelector } from '~/hooks/useLanguageSelector';
import { useSupportedLanguages } from '~/hooks/useSupportedLanguages';

import { useUILanguage } from '../LocaleProvider';

import { DoctorCard } from './DoctorCard';

const { Button } = ToucanComponents;

const title = 'User Info';
enum Label {
  UserId = 'User Id',
  Email = 'Email',
  Subscription = 'Subscription',
  SourceLanguage = 'Source Language',
  TargetLanguage = 'Target Language',
}
enum Value {
  NotApplicable = 'N/A',
  NotSet = 'Not Set',
}
const getSubscriptionText = (isPremiumSubscribed?: boolean) => (isPremiumSubscribed ? 'Premium' : 'None');

const NotLoggedInAlert = () => (
  <Alert severity="error">
    <AlertTitle>Looks like you’re not logged in.</AlertTitle>
  </Alert>
);

interface UserInfoProps {
  getSourceLanguageValue: (sourceLanguage?: string) => string;
  getTargetLanguageValue: (targetLanguage: Language) => string;
}

const StandardUserInfo = ({ getSourceLanguageValue, getTargetLanguageValue }: UserInfoProps) => {
  const { isInstalled, loading } = useExtension();
  const { uiLanguage } = useUILanguage();
  const { getLanguageId } = useLanguageInfo();
  const { data, loading: userLoading, refetch } = useGetExtensionUserQuery({
    skip: !isInstalled,
  });
  const id = data?.me?.id;
  const email = data?.me?.email;
  const isPremiumSubscribed = data?.me?.isPremiumSubscribed;
  const sourceLanguage = data?.me?.sourceLanguage;
  const targetLanguage = data?.me?.selectedLanguage as Language;
  const targetLanguageId = targetLanguage ? getLanguageId(targetLanguage) : null;
  const { updateSourceLanguage } = useLanguageSelector();
  const { supportedLanguages } = useSupportedLanguages();
  const hasMissMatchedLanguages =
    sourceLanguage && targetLanguageId
      ? !supportedLanguages[sourceLanguage as SupportedSourceLanguages]?.includes(targetLanguageId)
      : false;
  const isMissingLanguages = !sourceLanguage || !targetLanguage;

  if (!isInstalled || loading || userLoading) {
    return null;
  }
  return (
    <DoctorCard
      title={title}
      beforeContent={!id && <NotLoggedInAlert />}
      asideContent={
        (hasMissMatchedLanguages || isMissingLanguages) && (
          <Button
            size="small"
            onClick={async () => {
              try {
                trackEvent({ name: 'Doctor fix user language' });
                await updateSourceLanguage(uiLanguage);
                await refetch();
              } catch (e) {
                // Ignore error
              }
            }}
          >
            Fix
          </Button>
        )
      }
      rows={[
        {
          label: Label.UserId,
          value: id ?? Value.NotApplicable,
        },
        {
          label: Label.Email,
          value: email ?? Value.NotApplicable,
        },
        {
          label: Label.Subscription,
          value: getSubscriptionText(isPremiumSubscribed),
        },
        {
          label: Label.SourceLanguage,
          value: getSourceLanguageValue(sourceLanguage),
        },
        {
          label: Label.TargetLanguage,
          value: getTargetLanguageValue(targetLanguage),
        },
      ]}
    />
  );
};

const FirefoxFallbackUserInfo = ({ getSourceLanguageValue, getTargetLanguageValue }: UserInfoProps) => {
  const { isInstalledInFirefox, loading, userInfo } = useFirefoxExtension();
  const id = userInfo?.id;
  const email = userInfo?.email;
  const isPremiumSubscribed = userInfo?.isPremiumSubscribed;
  const sourceLanguage = userInfo?.sourceLanguage;
  const targetLanguage = userInfo?.selectedLanguage as Language;

  if (!isInstalledInFirefox || loading) {
    return null;
  }
  /**
   * TODO: Add a "Fix" button in asideContent that allows the user to update
   * their source language without making a Finch request from the website
   * (MEOW-880).
   */
  return (
    <DoctorCard
      title={title}
      beforeContent={!id && <NotLoggedInAlert />}
      rows={[
        {
          label: Label.UserId,
          value: id ?? Value.NotApplicable,
        },
        {
          label: Label.Email,
          value: email ?? Value.NotApplicable,
        },
        {
          label: Label.Subscription,
          value: getSubscriptionText(isPremiumSubscribed),
        },
        {
          label: Label.SourceLanguage,
          value: getSourceLanguageValue(sourceLanguage),
        },
        {
          label: Label.TargetLanguage,
          value: getTargetLanguageValue(targetLanguage),
        },
      ]}
    />
  );
};

export const UserInfo = () => {
  const { shouldFallBack } = useExtension();
  const { getLanguageInfoFromLanguageId, getLanguageInfo } = useLanguageInfo();
  const getSourceLanguageValue = (sourceLanguage?: string) =>
    sourceLanguage ? getLanguageInfoFromLanguageId(sourceLanguage)?.language ?? sourceLanguage : Value.NotSet;
  const getTargetLanguageValue = (targetLanguage: Language) =>
    targetLanguage ? getLanguageInfo(targetLanguage)?.language ?? targetLanguage : Value.NotSet;

  return shouldFallBack ? (
    <FirefoxFallbackUserInfo
      getSourceLanguageValue={getSourceLanguageValue}
      getTargetLanguageValue={getTargetLanguageValue}
    />
  ) : (
    <StandardUserInfo getSourceLanguageValue={getSourceLanguageValue} getTargetLanguageValue={getTargetLanguageValue} />
  );
};
